import { useMemo, useState } from "react";
import { useHistory } from "react-router";
import { Button, SettingsCard, Typography, useToast } from "@humansignal/ui";
import { useUpdatePageTitle, createTitleFromSegments } from "@humansignal/core";
import { modal } from "../../components/Modal/Modal";
import { useModalControls } from "../../components/Modal/ModalPopup";
import Input from "../../components/Form/Elements/Input/Input";
import { Space } from "../../components/Space/Space";
import { Spinner } from "../../components/Spinner/Spinner";
import { useAPI } from "../../providers/ApiProvider";
import { useProject } from "../../providers/ProjectProvider";
import { cn } from "../../utils/bem";
import { SettingsPageHeader } from "../../components/SettingsPageHeader/SettingsPageHeader";
import { useTranslation } from "../../i18n/LocaleProvider";

export const DangerZone = () => {
  const { t } = useTranslation();
  const { project, fetchProject } = useProject();
  const api = useAPI();
  const history = useHistory();
  const toast = useToast();
  const [processing, setProcessing] = useState(null);

  useUpdatePageTitle(createTitleFromSegments([project?.title, "Danger Zone"]));

  const showDangerConfirmation = ({ title, message, requiredWord, buttonText, onConfirm }) => {
    const isDev = process.env.NODE_ENV === "development";

    return modal({
      title,
      width: 600,
      allowClose: false,
      body: () => {
        const ctrl = useModalControls();
        const inputValue = ctrl?.state?.inputValue || "";

        return (
          <div>
            <Typography variant="body" size="medium" className="mb-tight">
              {message}
            </Typography>
            <Input
              label={`To proceed, type "${requiredWord}" in the field below:`}
              value={inputValue}
              onChange={(e) => ctrl?.setState({ inputValue: e.target.value })}
              autoFocus
              data-testid="danger-zone-confirmation-input"
              autoComplete="off"
            />
          </div>
        );
      },
      footer: () => {
        const ctrl = useModalControls();
        const inputValue = (ctrl?.state?.inputValue || "").trim().toLowerCase();
        const isValid = isDev || inputValue === requiredWord.toLowerCase();

        return (
          <Space align="end">
            <Button
              variant="neutral"
              look="outlined"
              onClick={() => ctrl?.hide()}
              data-testid="danger-zone-cancel-button"
            >
              Cancel
            </Button>
            <Button
              variant="negative"
              disabled={!isValid}
              onClick={async () => {
                await onConfirm();
                ctrl?.hide();
              }}
              data-testid="danger-zone-confirm-button"
            >
              {buttonText}
            </Button>
          </Space>
        );
      },
    });
  };

  const handleOnClick = (type) => async () => {
    // Restore needs no scary confirmation — it only re-opens the project.
    if (type === "restore") {
      setProcessing(type);
      try {
        await api.callApi("updateProject", { params: { pk: project.id }, body: { is_archived: false } });
        toast.show({ message: "Project restored." });
        await fetchProject?.(project.id, true);
      } catch (error) {
        toast.show({ message: `Error: ${error.message}`, type: "error" });
      } finally {
        setProcessing(null);
      }
      return;
    }
    // Accept-all-pending lives here (not the dashboard) because it's a
    // non-undoable, PAY-AFFECTING bulk action: every pending annotation
    // is accepted and the annotators credited in the contribution
    // ledger. The dialog shows the exact counts before the typed
    // confirmation so nobody accepts hundreds of items blind.
    if (type === "accept_all") {
      setProcessing(type);
      const preview = await api.callApi("projectReviewAcceptAllPreview", {
        params: { pk: project.id },
        suppressError: true,
      });
      setProcessing(null);
      const pendingCount = preview?.pending ?? 0;
      const escalatedCount = preview?.escalated ?? 0;
      if (!pendingCount) {
        toast.show({
          message: escalatedCount
            ? `Nothing to accept. ${escalatedCount} flagged item(s) still need review.`
            : "Nothing to accept, there are no pending annotations.",
        });
        return;
      }
      showDangerConfirmation({
        title: "Accept All Pending Annotations",
        message: (
          <>
            You are about to accept <strong>{pendingCount}</strong> pending annotation
            {pendingCount === 1 ? "" : "s"} in <strong>{project.title}</strong> and credit the annotators in the
            contribution ledger — this feeds the pay process and cannot be bulk-undone.
            {escalatedCount ? (
              <>
                {" "}
                <strong>{escalatedCount}</strong> flagged item{escalatedCount === 1 ? "" : "s"} will be skipped and
                still need review.
              </>
            ) : null}
          </>
        ),
        requiredWord: "accept",
        buttonText: `Accept ${pendingCount} Annotation${pendingCount === 1 ? "" : "s"}`,
        onConfirm: async () => {
          setProcessing(type);
          try {
            const res = await api.callApi("projectReviewAcceptAll", {
              params: { pk: project.id },
              suppressError: true,
            });
            if (res && !res.error) {
              toast.show({
                message:
                  `Accepted ${res.accepted}, credited ${res.credited}` +
                  (res.skipped_escalated ? `; ${res.skipped_escalated} flagged item(s) still need review.` : "."),
              });
            } else {
              toast.show({ type: "error", message: res?.error || "Accept-all failed." });
            }
          } finally {
            setProcessing(null);
          }
        },
      });
      return;
    }

    const actionConfig = {
      archive: {
        title: "Archive Project",
        message: (
          <>
            You are about to archive <strong>{project.title}</strong>. It disappears from every project list, labeling
            and review stop, and nothing is deleted — restore it any time from the Archived filter.
          </>
        ),
        requiredWord: "archive",
        buttonText: "Archive Project",
      },
      reset_cache: {
        title: "Reset Cache",
        message: (
          <>
            You are about to reset the cache for <strong>{project.title}</strong>. This action cannot be undone.
          </>
        ),
        requiredWord: "cache",
        buttonText: "Reset Cache",
      },
      tabs: {
        title: "Drop All Tabs",
        message: (
          <>
            You are about to drop all tabs for <strong>{project.title}</strong>. This action cannot be undone.
          </>
        ),
        requiredWord: "tabs",
        buttonText: "Drop All Tabs",
      },
      project: {
        title: "Delete Project",
        message: (
          <>
            You are about to delete the project <strong>{project.title}</strong>. This action cannot be undone.
          </>
        ),
        requiredWord: "delete",
        buttonText: "Delete Project",
      },
    };

    const config = actionConfig[type];

    if (!config) {
      return;
    }

    showDangerConfirmation({
      ...config,
      onConfirm: async () => {
        setProcessing(type);
        try {
          if (type === "reset_cache") {
            await api.callApi("projectResetCache", {
              params: {
                pk: project.id,
              },
            });
            toast.show({ message: t("pages.settings.danger_zone.cache_reset_successfully") });
          } else if (type === "tabs") {
            await api.callApi("deleteTabs", {
              body: {
                project: project.id,
              },
            });
            toast.show({ message: t("pages.settings.danger_zone.all_tabs_dropped_successfully") });
          } else if (type === "archive") {
            await api.callApi("updateProject", { params: { pk: project.id }, body: { is_archived: true } });
            toast.show({ message: "Project archived. Find it under the Archived filter." });
            history.replace("/projects");
          } else if (type === "project") {
            await api.callApi("deleteProject", {
              params: {
                pk: project.id,
              },
            });
            toast.show({ message: t("pages.settings.danger_zone.project_deleted_successfully") });
            history.replace("/projects");
          }
        } catch (error) {
          toast.show({ message: `Error: ${error.message}`, type: "error" });
        } finally {
          setProcessing(null);
        }
      },
    });
  };

  const buttons = useMemo(
    () => [
      {
        type: "annotations",
        disabled: true, //&& !project.total_annotations_number,
        label: `Delete ${project.total_annotations_number} Annotations`,
      },
      {
        type: "tasks",
        disabled: true, //&& !project.task_number,
        label: `Delete ${project.task_number} Tasks`,
      },
      {
        type: "predictions",
        disabled: true, //&& !project.total_predictions_number,
        label: `Delete ${project.total_predictions_number} Predictions`,
      },
      {
        type: "accept_all",
        help:
          "Accept every pending annotation in one action and credit the annotators in the contribution ledger. " +
          "straight to the pay process. Flagged work (crowd reports, QC routing, low agreement) is skipped and " +
          "still needs review. Cannot be bulk-undone.",
        label: "Accept All Pending Annotations",
      },
      {
        type: "reset_cache",
        help:
          "Reset Cache may help in cases like if you are unable to modify the labeling configuration due " +
          "to validation errors concerning existing labels, but you are confident that the labels don't exist. You can " +
          "use this action to reset the cache and try again.",
        label: "Reset Cache",
      },
      {
        type: "tabs",
        help: "If the Data Manager is not loading, dropping all Data Manager tabs can help.",
        label: "Drop All Tabs",
      },
      project.is_archived
        ? {
            type: "restore",
            help: "This project is archived: hidden from lists and closed to new work. Restore puts it back exactly as it was.",
            label: "Restore Project",
          }
        : {
            type: "archive",
            help:
              "Park a finished or paused project without deleting anything: hidden from every list and closed to new " +
              "work, with all data, members and exports kept. Restore any time.",
            label: "Archive Project",
          },
      {
        type: "project",
        help: "Deleting a project removes all tasks, annotations, and project data from the database.",
        label: "Delete Project",
      },
    ],
    [project],
  );

  return (
    <div className={cn("settings-page").toClassName()}>
      <SettingsPageHeader
        title={t("pages.settings.danger_zone.danger_zone")}
        subtitle={t("pages.settings.danger_zone.perform_these_actions_at_your_own_risk_a")}
      />

      {project.id ? (
        buttons.map((btn) => {
          const waiting = processing === btn.type;
          const disabled = btn.disabled || (processing && !waiting);
          if (btn.disabled === true) return null;

          return (
            <SettingsCard key={btn.type} title={btn.label} description={btn.help}>
              <Button
                variant={btn.type === "restore" ? "primary" : "negative"}
                look="outlined"
                disabled={disabled}
                waiting={waiting}
                onClick={handleOnClick(btn.type)}
              >
                {btn.label}
              </Button>
            </SettingsCard>
          );
        })
      ) : (
        <div style={{ display: "flex", justifyContent: "center", marginTop: 32 }}>
          <Spinner size={32} />
        </div>
      )}
    </div>
  );
};

DangerZone.title = "Danger Zone";
DangerZone.path = "/danger-zone";
